import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import {
  InputNumber, Row, Col, message, Space
} from 'antd';
import {
  minLaserPowerState,
  maxLaserPowerState,
  minIntegrationTimeState,
  maxIntegrationTimeState,
  centerState
} from '../stateManagement/controlButtonState';
import { mapState } from '../stateManagement/commonState';
import { scanningState, scanTypeState } from '../stateManagement/processState';

export const SettingsButtons = () => {
  const [laserPowerError, setLaserPowerError] = useState('');
  const [integrationTimeError, setIntegrationTimeError] = useState('');
  const [, setMinLaserPower] = useAtom(minLaserPowerState);
  const [maxLaserPower, setMaxLaserPower] = useAtom(maxLaserPowerState);
  const [scanning] = useAtom(scanningState);
  const [, setMinIntegrationTime] = useAtom(minIntegrationTimeState);
  const [maxIntegrationTime, setMaxIntegrationTime] = useAtom(maxIntegrationTimeState);
  const [map] = useAtom(mapState);
  const [center, setCenter] = useAtom(centerState);
  const [scanType] = useAtom(scanTypeState);
  const [laserPowerDisplay, setLaserPowerDisplay] = useState(maxLaserPower);
  const [integrationTimeDisplay, setIntegrationTimeDisplay] = useState(maxIntegrationTime);

  useEffect(() => { setLaserPowerDisplay(maxLaserPower); }, [maxLaserPower]);
  useEffect(() => { setIntegrationTimeDisplay(maxIntegrationTime); }, [maxIntegrationTime]);

  const laserPowerChangeHandler = (value) => {
    setLaserPowerDisplay(value);
    if (isNaN(value) || value === null) {
      setLaserPowerError('Please input a number');
      return;
    } else if (value < 45 || value > 450) {
      setLaserPowerError('Value must be between 45 and 450');
      return;
    }
    setLaserPowerError('');
    setMinLaserPower(value);
    setMaxLaserPower(value);
  }

  const laserPowerBlurHandler = () => {
    if (laserPowerError) {
      setLaserPowerDisplay(maxLaserPower);
      setLaserPowerError('');
    }
  }

  const integrationTimeChangeHandler = (value) => {
    setIntegrationTimeDisplay(value);
    if (isNaN(value) || value === null) {
      setIntegrationTimeError('Please input a number');
      return;
    } else if (value < 500 || value > 120000) {
      setIntegrationTimeError('Value must be between 500 and 120000');
      return;
    } else if (value % 500 !== 0) {
      setIntegrationTimeError('Value must be a multiple of 500');
      return;
    }
    setIntegrationTimeError('');
    setMinIntegrationTime(value);
    setMaxIntegrationTime(value);
  }

  const integrationTimeBlurHandler = () => {
    if (integrationTimeError) {
      setIntegrationTimeDisplay(maxIntegrationTime);
      setIntegrationTimeError('');
    }
  }

  const centerCoordinateChangeHandler = (value, axis) => {
    value = Number(value);
    setCenter(prevCenter => {
      let newCenter = { ...prevCenter };
      newCenter[axis] = value;
      message.success({ content: `Center coordinate updated. (${newCenter.x}, ${newCenter.y}, ${newCenter.z})`, duration: 3 });
      return newCenter
    });
  }

  return (
    <>
      <Row justify="center" align="middle" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} style={{ padding: '10px 0' }}>
        <Col span={7} className="gutter-row">
          <Row justify="center">Laser Power</Row>
          {laserPowerError && <Row justify="center" style={{ color: 'red' }}>{laserPowerError}</Row>}
          <InputNumber
            value={laserPowerDisplay}
            addonAfter='mW'
            onChange={value => laserPowerChangeHandler(Number(value))}
            onBlur={laserPowerBlurHandler}
            controls={false}
            style={{ width: '100%' }}
            disabled={scanning || map.length || scanType === 'reference'}
          />
        </Col>
        <Col span={7} className="gutter-row">
          <Row justify="center">Integration Time</Row>
          {integrationTimeError && <Row justify="center" style={{ color: 'red' }}>{integrationTimeError}</Row>}
          <InputNumber
            value={integrationTimeDisplay}
            addonAfter='ms'
            onChange={value => integrationTimeChangeHandler(Number(value))}
            onBlur={integrationTimeBlurHandler}
            style={{ width: '100%' }}
            disabled={scanning || map.length || scanType === 'reference'}
          />
        </Col>
        <Col span={7} className="gutter-row">
          <Row justify="center">Center Coordinate</Row>
          <Space.Compact direction={'vertical'} block>
            <InputNumber
              value={center.x}
              min={0}
              max={25}
              addonBefore='x'
              addonAfter='mm'
              onChange={value => centerCoordinateChangeHandler(value, 'x')}
              style={{ width: '100%' }}
              disabled={scanning || map.length || scanType === 'reference'}
            />
            <InputNumber
              value={center.y}
              min={0}
              max={30}
              addonBefore='y'
              addonAfter='mm'
              onChange={value => centerCoordinateChangeHandler(value, 'y')}
              style={{ width: '100%' }}
              disabled={scanning || map.length || scanType === 'reference'}
            />
            <InputNumber
              value={center.z}
              min={0}
              max={20}
              addonBefore='z'
              addonAfter='mm'
              onChange={value => centerCoordinateChangeHandler(value, 'z')}
              style={{ width: '100%' }}
              disabled={scanning || map.length || scanType === 'reference'}
            />
          </Space.Compact>
        </Col>
      </Row>
    </>
  );
};
